// Copyright (c) 2025 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, TextInput } from 'react-native';
import { Redirect, useRootNavigationState } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';

import { Text, View } from '@/components/Themed';
import { useI18n } from '@/i18n';
import { getSharedBffWebSocket, GolfCourseChangedEvent, GolfCourseUpsertRejectedEvent } from '@/services/bffWebSocket';
import { useUser } from './user-context';
import { getApiBaseUrl } from '@/services/apiBaseUrl';

const HOLE_COUNT = 18;

const createRequestId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    const uuid = (crypto as { randomUUID?: () => string }).randomUUID?.();
    if (uuid) return uuid;
  }
  return `req-${Date.now()}`;
};

type HoleRow = { number: number; par: string; strokeIndex: string };

type SaveState = {
  status: 'idle' | 'saving' | 'success' | 'error';
  message?: string | null;
};

export default function ConfigCourseScreen() {
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const isFocused = useIsFocused();
  const { t } = useI18n();
  const { user } = useUser();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const socketRef = useRef(getSharedBffWebSocket(apiBaseUrl));
  const pendingRequestIdRef = useRef<string | null>(null);
  const [clubName, setClubName] = useState('');
  const [courseName, setCourseName] = useState('');
  const [holes, setHoles] = useState<HoleRow[]>(
    Array.from({ length: HOLE_COUNT }, (_, idx) => ({
      number: idx + 1,
      par: '4',
      strokeIndex: String(idx + 1),
    }))
  );
  const [saveState, setSaveState] = useState<SaveState>({ status: 'idle', message: null });

  useEffect(() => {
    const socket = socketRef.current;
    socket.setAuthToken(user?.token ?? null);
    if (isFocused) {
      socket.setSubscriptionContext({ roundId: null, tourId: null });
    }
    const unsubscribe = socket.onMessage((message) => {
      const courseChanged = message as GolfCourseChangedEvent;
      if (courseChanged.type === 'golfCourse.changed') {
        const requestId = courseChanged.requestId ?? courseChanged.RequestId;
        if (!requestId || pendingRequestIdRef.current !== requestId) {
          return;
        }
        pendingRequestIdRef.current = null;
        setSaveState({
          status: 'success',
          message: t('courseConfig.saveSuccess', { id: courseChanged.courseId ?? '' }),
        });
        return;
      }
      const rejected = message as GolfCourseUpsertRejectedEvent;
      if (rejected.type === 'golfCourse.upsert.rejected') {
        if (!rejected.requestId || pendingRequestIdRef.current !== rejected.requestId) {
          return;
        }
        pendingRequestIdRef.current = null;
        setSaveState({ status: 'error', message: t('courseConfig.saveRejected') });
      }
    });
    return () => unsubscribe();
  }, [isFocused, t, user?.token]);

  if (!isNavigationReady) return null;
  if (!user) return <Redirect href="/login" />;

  const updateHole = (index: number, patch: Partial<HoleRow>) => {
    setHoles((prev) => prev.map((hole, idx) => (idx === index ? { ...hole, ...patch } : hole)));
  };

  const validateHoles = () => {
    const parsed = holes.map((hole) => ({
      number: hole.number,
      par: Number.parseInt(hole.par, 10),
      strokeIndex: Number.parseInt(hole.strokeIndex, 10),
    }));
    if (parsed.some((hole) => !Number.isFinite(hole.par) || hole.par <= 0)) {
      return { ok: false, message: t('courseConfig.invalidPar') };
    }
    if (parsed.some((hole) => !Number.isFinite(hole.strokeIndex) || hole.strokeIndex <= 0)) {
      return { ok: false, message: t('courseConfig.invalidStrokeIndex') };
    }
    return { ok: true, holes: parsed } as const;
  };

  const onSave = () => {
    if (!clubName.trim()) {
      setSaveState({ status: 'error', message: t('courseConfig.clubRequired') });
      return;
    }
    const validation = validateHoles();
    if (!validation.ok) {
      setSaveState({ status: 'error', message: validation.message });
      return;
    }

    const requestId = createRequestId();
    const payload = {
      type: 'golfCourse.upsert',
      requestId,
      source: 'user',
      userId: user.id,
      clubName: clubName.trim(),
      courseName: courseName.trim(),
      holes: validation.holes,
    };

    const socket = socketRef.current;
    const sent = socket.send(payload);
    pendingRequestIdRef.current = requestId;
    setSaveState({
      status: 'saving',
      message: sent ? t('courseConfig.saving') : t('courseConfig.waitingConnection'),
    });
  };

  return (
    <ScrollView contentContainerStyle={styles.container} keyboardShouldPersistTaps="handled">
      <View style={styles.card}>
        <Text style={styles.title}>{t('courseConfig.title')}</Text>
        <View style={styles.inputRow}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>{t('courseConfig.clubLabel')}</Text>
            <TextInput
              style={styles.input}
              value={clubName}
              onChangeText={setClubName}
              placeholder={t('courseConfig.clubPlaceholder')}
              placeholderTextColor="rgba(226,232,240,0.6)"
            />
          </View>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>{t('courseConfig.courseLabel')}</Text>
            <TextInput
              style={styles.input}
              value={courseName}
              onChangeText={setCourseName}
              placeholder={t('courseConfig.coursePlaceholder')}
              placeholderTextColor="rgba(226,232,240,0.6)"
            />
          </View>
        </View>
        <View style={styles.tableHeader}>
          <Text style={[styles.tableCell, styles.holeCell]}>{t('courseConfig.holeHeader')}</Text>
          <Text style={[styles.tableCell, styles.parCell]}>{t('courseConfig.parHeader')}</Text>
          <Text style={[styles.tableCell, styles.strokeCell]} numberOfLines={1} ellipsizeMode="tail">
            {t('courseConfig.strokeIndexHeader')}
          </Text>
        </View>
        {holes.map((hole, index) => (
          <View key={hole.number} style={styles.tableRow}>
            <Text style={[styles.tableCell, styles.holeCell]}>{hole.number}</Text>
            <TextInput
              style={[styles.tableCell, styles.parCell, styles.tableInput]}
              value={hole.par}
              onChangeText={(value) => updateHole(index, { par: value })}
              keyboardType="number-pad"
            />
            <TextInput
              style={[styles.tableCell, styles.strokeCell, styles.tableInput]}
              value={hole.strokeIndex}
              onChangeText={(value) => updateHole(index, { strokeIndex: value })}
              keyboardType="number-pad"
            />
          </View>
        ))}
        <Pressable
          style={styles.saveButton}
          onPress={onSave}
          disabled={saveState.status === 'saving' || !clubName.trim()}
        >
          {saveState.status === 'saving' ? <ActivityIndicator color="#0f172a" /> : null}
          <Text style={styles.saveButtonText}>{t('courseConfig.saveButton')}</Text>
        </Pressable>
        {saveState.message ? (
          <Text style={saveState.status === 'error' ? styles.errorText : styles.statusText}>{saveState.message}</Text>
        ) : null}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    padding: 20,
    gap: 12,
    backgroundColor: '#0c1328',
    alignItems: 'center',
  },
  card: {
    width: '100%',
    maxWidth: 420,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#24346b',
    backgroundColor: '#0f1c3c',
    padding: 16,
    gap: 12,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: '#f8fafc',
  },
  inputRow: {
    gap: 12,
  },
  inputGroup: {
    gap: 6,
  },
  label: {
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    color: '#d5e1ff',
  },
  input: {
    borderWidth: 1,
    borderColor: '#33456d',
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontSize: 15,
    color: '#e5edff',
    backgroundColor: '#0b1224',
  },
  tableHeader: {
    flexDirection: 'row',
    paddingVertical: 8,
    columnGap: 12,
    borderBottomWidth: 1,
    borderColor: '#33456d',
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 1,
    columnGap: 12,
    borderBottomWidth: 1,
    borderColor: 'rgba(51,69,109,0.6)',
  },
  tableCell: {
    fontSize: 14,
    color: '#e2e8f0',
  },
  holeCell: {
    width: 60,
  },
  parCell: {
    width: 80,
  },
  strokeCell: {
    width: 70,
  },
  tableInput: {
    borderWidth: 1,
    borderColor: '#33456d',
    borderRadius: 10,
    paddingHorizontal: 10,
    paddingVertical: 8,
    backgroundColor: '#0b1224',
    color: '#e5edff',
  },
  saveButton: {
    marginTop: 12,
    paddingVertical: 12,
    borderRadius: 12,
    backgroundColor: '#f6d98a',
    alignItems: 'center',
    gap: 6,
  },
  saveButtonText: {
    fontSize: 15,
    fontWeight: '700',
    color: '#0f172a',
  },
  statusText: {
    color: '#d5e1ff',
  },
  errorText: {
    color: '#b91c1c',
  },
});
